/**
 * Pure helpers for the card's RECENT ROLES section and the experience
 * drawer's WORK EXPERIENCE timeline (STN client round 2026-08).
 *
 * Source data is MAPS.PersonJob via the API's employmentHistory field —
 * already ordered Current-first then StartDate DESC, so "top N" here is a
 * plain slice, never a re-sort. Dates are raw source strings and often null
 * ("Unknown" in the feed); absence is rendered as absence, never as a gap
 * flag (standing "honest blanks" policy).
 */
import type { MapsEmploymentRole } from '../models/maps.types';

/** How many roles the card shows before deferring to the drawer
 *  (3 → 2 on the 2026-08-12 client ask, to reduce card whitespace). */
export const CARD_ROLE_COUNT = 2;

/** The card's top slice (order preserved from the API). */
export function TopRoles(roles: readonly MapsEmploymentRole[] | null | undefined): MapsEmploymentRole[] {
    return (roles ?? []).slice(0, CARD_ROLE_COUNT);
}

/** How many roles overflow the card ("+ N earlier roles" → drawer). */
export function EarlierRoleCount(roles: readonly MapsEmploymentRole[] | null | undefined): number {
    return Math.max(0, (roles ?? []).length - CARD_ROLE_COUNT);
}

/** Leading 4-digit year of a raw source date string; null when absent/unparseable. */
function yearOf(date: string | null | undefined): string | null {
    const m = /^(\d{4})/.exec(date ?? '');
    return m ? m[1] : null;
}

/**
 * Human period label for one role: "2015–present", "1998–2003", "2010", or
 * null when the source carries no usable dates (the row renders without a
 * period rather than with a placeholder).
 */
export function FormatRolePeriod(role: MapsEmploymentRole): string | null {
    const start = yearOf(role.startDate);
    const end = yearOf(role.endDate);
    if (start && role.isCurrent) return `${start}–present`;
    if (start && end) return start === end ? start : `${start}–${end}`;
    return start || end || null;
}

/** "Org · 2015–present" secondary line; org-less rows fall back to the period alone. */
export function FormatRoleMeta(role: MapsEmploymentRole): string | null {
    const period = FormatRolePeriod(role);
    const org = role.organization?.trim() || null;
    if (org && period) return `${org} · ${period}`;
    return org || period;
}
